import { RouteName } from "constant/routeNames";
import { decodeHtmlEntities } from "constant/htmlEntities";
import { useState } from "react";
import { BsPlayCircle, BsPauseCircle } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { addMusicToPlayer } from "redux/AudioPlayerRedux";
import AddSongToQueue from "./AddSongToQueue";
import LazyLoadImg from "components/LazyLoadImg";
import AddSongToFav from "./AddSongToFav";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { FaCrown } from "react-icons/fa";
import { ContentType } from "services/subscription/types";
import { usePremiumAccess } from "hooks/usePremiumAccess";
import PremiumContentModal from "components/PremiumContentModal";

const MusicCard = ({ song, is_creator }: any) => {
  const loggedInUser = useSelector((state: any) => state?.user?.currentUser);
  const dispatch = useDispatch<any>();
  const [active, setActive] = useState(false);
  const [hover, setHover] = useState(false);

  const { isModalOpen, setIsModalOpen, checkAccess } = usePremiumAccess();

  const { currentAudio, isPlaying } = useSelector(
    (state: any) => state.audio,
  );
  const isActive = currentAudio?.id != null && currentAudio?.id === song?.id;
  const isThisPlaying = isPlaying && isActive;

  const isPremiumContent = song?.plan_id === 2;
  const isPlusUser = loggedInUser?.is_subscribed;

  const popupTrigger = () => {
    setActive((val: boolean) => !val);
  };

  const handleMusicPlayer = async () => {
    const access = await checkAccess({
      contentType: ContentType.SONGS,
      id: song?.id,
      isPremiumContent,
      isCreator: is_creator,
    });
    if (!access) return;

    dispatch(addMusicToPlayer(song));
  };

  const packageObject: any = {
    id: song?.service_id || song?.id,
    entitlement_id: song?.entitlement_id || "",
    title: song?.title,
    price: song?.price || "",
    thumbnail: song?.artwork_url || "",
    created_at: song?.created_at || "",
    description: song?.description || "",
    is_subscribed: song?.is_subscribed || false,
    role_id: song?.plan_id,
    slug: song?.slug,
  };

  return (
    <>
      <PremiumContentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        isCreator={is_creator}
        packageObject={packageObject}
      />

      <div className="group block w-full cursor-pointer rounded-xl px-2 py-2 text-white shadow-lg transition-all duration-300 ease-out hover:-translate-y-1 hover:bg-slate-800 hover:text-slate-300 hover:shadow-xl">
        <div
          className={`bg-img white-text relative aspect-square w-full overflow-hidden rounded-lg ring-1 transition-all duration-300 group-hover:ring-2 group-hover:ring-[var(--red)]/40 ${
            isActive ? "ring-2 ring-[var(--red)]/70" : "ring-white/5"
          }`}
        >
          <LazyLoadImg
            imgSrc={song?.artwork_url}
            imgAlt={song?.title}
            imgHeight={"100%"}
            imgWidth={"100%"}
            wrapperProps={{
              style: {
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                display: "block",
              },
            }}
            className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
            }}
          />

          {/* centerlize play / playing indicator */}
          <button
            className="add-button absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 items-center rounded-full p-1 hover:shadow-2xl"
            onClick={handleMusicPlayer}
          >
            {isThisPlaying ? (
              <>
                {/* Pause icon on hover, animated equalizer otherwise */}
                <BsPauseCircle className="action-icon-size text-md my-auto hidden h-12 w-12 text-red-600 duration-300 ease-in-out group-hover:block" />
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-black/50 group-hover:hidden">
                  <span className="flex h-5 items-end gap-[3px]">
                    <span className="eq-bar" style={{ animationDelay: "0s" }} />
                    <span className="eq-bar" style={{ animationDelay: "0.2s" }} />
                    <span className="eq-bar" style={{ animationDelay: "0.4s" }} />
                  </span>
                </span>
              </>
            ) : (
              <BsPlayCircle className="action-icon-size text-md my-auto h-12 w-12 duration-300 ease-in-out hover:text-red-600" />
            )}
          </button>

          {/* Premium pill */}
          {isPremiumContent && (
            <div className="absolute left-2 top-2 z-10 flex items-center gap-1 rounded-full border border-white/15 bg-black/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white shadow-lg backdrop-blur-md">
              <FaCrown className="text-[10px] text-[var(--red)]" />
              <span>Premium</span>
            </div>
          )}

          {/* card inside content */}
          <div className="absolute inset-x-0 bottom-0 flex justify-between p-2">
            <div className="add-button items-center rounded-full p-1">
              <AddSongToQueue song={song} toolTiId={`new-released-card`} />
            </div>
            {loggedInUser && (
              <div
                className="add-button items-center rounded-full p-1"
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
              >
                <AddSongToFav song={song} musicCard={true} hover={hover} />
              </div>
            )}
          </div>
        </div>
        <div className="relative top-1">
          <div className="my-[4px]">
            <Link to={RouteName.MusicTrackRoute.replace(":id", song.id)}>
              <p
                className={`line-clamp-2 text-[14px] font-[600] leading-tight transition-colors group-hover:text-white ${
                  isActive ? "text-[var(--red)]" : ""
                }`}
                
              >
                {decodeHtmlEntities(song?.title.replace(/_/g, " "))}
              </p>

              <div className="text-sm">
                <ReactTooltip
                  id={`song-${song.id}`}
                  place="top"
                  content={song.title}
                />
              </div>
            </Link>
            {song?.artists?.[0]?.slug ? (
              <Link
                to={RouteName.VideoCreatorHomeRoute.replace(
                  ":slug",
                  `@${song.artists[0].slug}`,
                )}
              >
                <p className="mt-[5px] text-[11px] font-[400] text-white/60 hover:text-zinc-100 hover:underline">
                  {song.artists[0].name}
                </p>
              </Link>
            ) : null}
          </div>
        </div>
      </div>

      {/* Scoped styles for the equalizer animation */}
      <style>{`
        .eq-bar {
          width: 3px;
          height: 100%;
          border-radius: 2px;
          background: #ef4444;
          animation: eqPulse 0.9s ease-in-out infinite;
        }
        @keyframes eqPulse {
          0%, 100% { transform: scaleY(0.3); }
          50% { transform: scaleY(1); }
        }
      `}</style>
    </>
  );
};

export default MusicCard;